/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./VIPRDecoratable',
	'./VIPRFormatter',
	'../../../widgets/livewidget/nls/StringResources'
], function(_, VIPRDecoratable, VIPRFormatter, StringResources) {

	// implements IItem
	class VIPRItem extends VIPRDecoratable {
		constructor(item, context, content) {
			super(item, context);

			this.item = item;
			this.content = content;
			this.context = context;
			this.formatter = context.formatter;	//The formatter feature.
			this._tuples = [];
			this.dataset = context.dataset;
			this._applyFormatFromContext();

		}

		destroy() {
			this.context = null;
			this.dataset = null;
			this._datapoints = null;
			this._tuples = null;
		}

		_applyFormatFromContext() {
			//This function should be called ONLY from the constructor of a VIPR Interface as it uses global context.
			//the context member of an object is valid during creation of the VIPR items (as the chart is being built).
			//The dataItem and tupleItem are reliable at that time and can be used to obtain an appropriate formatter.
			const viprFormatter = new VIPRFormatter(this.context.index.dataitem, this.context.index.tupleitem,
				{
					formatter: this.formatter,
					dataset: this.dataset,
					dataItemFormats: this.context.index.dataItemFormats
				}
			);
			return this._calculateformattedValue(viprFormatter);
		}

		_recalculatedFormattedValue() {
			//This function is similar to _applyFormatFromContext except that it uses
			//the indexes for this VIPRItem instance (initialized in the constructor) 
			//to determine the dataItem and tuple indexes not the context.
			const viprDataItemIndex = this.indexes.dataitem;
			const viprDataItem = this.dataset.dataItems[viprDataItemIndex];
			const viprFormatter = new VIPRFormatter(
				this.indexes.dataitem,
				this.indexes.tupleitem,
				//The VIPRFormatter needs to have access to the formatter feature, dataset & dataItemFormats cached with the item
				{
					formatter: this.formatter,
					dataset: this.dataset,
					dataItemFormats: viprDataItem && viprDataItem.dataItemFormats
				}
			);
			return this._calculateformattedValue(viprFormatter);
		}

		/**
		 * We are checking here if the aggregateTypeVisible toggler is on/off for multiMeasures
		 * Regardless of the formatted string, this unformattedValue will remain constant which allows usage such as color consistency (fred is red)
		 */
		_calculateformattedValue(viprFormatter) {
			if (this.item.value === undefined || this.item.label === null) {
				this.formattedValue = viprFormatter.format(null);
				this.unformattedValue = this.formattedValue;
			} else if (this.item.aggregate) {
				let aggregateTypeVisible = !!(this.content.getPropertyValue('widget.legend.aggregateTypeVisible'));
				if (aggregateTypeVisible) {
					this.formattedValue = viprFormatter.format(StringResources.get('multiMeasureLabel',{ 'measureName':this.item.label.toString(),'aggregate':StringResources.get(this.item.aggregate.toString()) }));
					this.unformattedValue = this.item.label + this.item.aggregate;
				} else {
					this.formattedValue = viprFormatter.format(StringResources.get('measureLabel',{ 'measureName':this.item.label.toString() }));
					this.unformattedValue = this.item.label + this.item.aggregate;
				}
			} else {
				this.formattedValue = viprFormatter.format(this.item.label.toString());
				this.unformattedValue = this.item.label;
			}
		}

		addTuple(tuple) {
			this._tuples.push(tuple);
			this._datapoints = null;
		}

		get datapoints() {
			if ( !this._datapoints ) {
				this._datapoints = [];
				for ( let i = 0; i < this._tuples.length; i++ ) {
					const tuple = this._tuples[i];
					for ( let j = 0; j < tuple.datapoints.length; j++ ) {
						this._datapoints.push(tuple.datapoints[j]);
					}
				}
			}
			return this._datapoints;
		}

		getUniqueName() {
			return (this.item.value === undefined || this.item.value === null) ? this.item.value : this.item.value.toString();
		}

		getCaption() {
			if ( this.item.aggregate ) {
				const currentAggregationPropertyValue = this.content.getPropertyValue('widget.legend.aggregateTypeVisible');
				if (currentAggregationPropertyValue != this.aggregationPropertyValue) {
					this._recalculatedFormattedValue();
					this.aggregationPropertyValue = currentAggregationPropertyValue;
				}
			}
			if ( !this.formattedValue ) {
				this._recalculatedFormattedValue();
			}
			return this.formattedValue;
		}

		getParent() {
			return this.item.p;
		}

		decorate(action, value) {
			super.decorate(...arguments);
			const datapoints = this.datapoints;
			if (datapoints.length > 0) {
				this.context.version++;
				// decorate the datapoints
				for ( let i = 0; i < datapoints.length; i++ ) {
					datapoints[i].decorate(action, value);
				}
			}
		}
	}

	return VIPRItem;
});